// The school's own locations, and the one marker asset that renders them.
//
// ── WHY THIS IS BUILD CONFIG AND NOT SERVER DATA ────────────────────────────
//
// `School` (educe-tec `core/storage/db/model/school/School.java`) carries a name, a
// tenant id, an insurance id, a logo and a settings blob — NO coordinates. The only
// place the estate records where a school physically IS, is `DEFAULT_MAP_CENTER` in
// each variant's `build.config.js`, so that is where its campuses go too. The cost is
// named rather than hidden: changing a campus list needs a frontend release, exactly
// as changing the map centre does today.
//
// ── THE FALLBACK IS THE MAP CENTRE, AND IT IS NOT A GUESS ───────────────────
//
// Matt, 2026-09-07: "for most schools it will be the same as the Center location, but
// some schools have multiple campuses and the center location tends to be between
// them or on one of them (main campus)." So a single-campus school needs no config at
// all and gets its marker the day this ships; the array exists for the schools where
// the centre is deliberately NOT a building. Declaring one is therefore always an act
// of CORRECTION, never a prerequisite — which is what keeps this from being 24 config
// commits before anybody sees a marker.
//
// ── IT DRAWS, AND IT DOES NOTHING ELSE ──────────────────────────────────────
//
// No click, no drag, no hit test (`pointer-events: none` on V2's overlay,
// `clickable: false` on the legacy `Marker`), and it is deliberately EXCLUDED from
// every viewport fit: `getMapCenter` / `getMapZoomLevel` in `./helpers.js` derive
// bounds from the `markers` prop, and folding a campus into that array would drag the
// Overview map's fit away from the buses it exists to show.

import { BuildConfig } from 'env/env.config';

// 48, not the 32 a stop pin uses, and the difference is load-bearing. The school
// station IS a stop on most routes, so the badge and a numbered pin land on the same
// coordinate — with the badge UNDER the pin (`SCHOOL_Z_INDEX`, so the pin keeps its
// order number). At 40px a 32px circle covered all of it but four barely-visible
// corners, which for a single-campus school — the fallback case, and the one with no
// caption to explain itself — is a marker that is simply not there. At 48 the rounded
// square's corners ring clearly around the pin.
export const SCHOOL_MARKER_PX = 48;
// The gap between the BOTTOM of the badge and the top of its caption.
//
// A GAP, deliberately, rather than an offset: both surfaces measure from the icon
// image's top-left (V2's wrapper holds the <img> at 0,0; the legacy `labelOrigin` is
// in icon-image coordinates), so `size + gap` is one expression valid from one
// origin on both. The first version of this shipped a centre-relative offset that
// only ONE of the two converted, and the V2 caption printed across the bottom third
// of its own badge.
export const SCHOOL_LABEL_GAP_PX = 6;

// Neither the wayto blue (#025c91) nor the wayback amber (#F4A03A): a landmark that
// shared a direction's hue would read as a stop on that direction.
const SCHOOL_BADGE_COLOR = '#3F3D8C';
// MUI's `School` icon path, 24x24 viewBox — the mortarboard.
const SCHOOL_GLYPH = 'M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3 1 9l11 6 9-4.91V17h2V9L12 3z';

// ONE asset for BOTH surfaces. V2 renders it as an <img> inside a DomMarker; the
// legacy maps hand the same string to `Marker`'s `icon.url`. Inline rather than a
// file under `assets/`, because `MapMarker` resolves its icons as runtime URLs
// ('assets/images/marker.png') while V2's OverlayView goes through no bundler at all
// — a data URI is the one form both take with no build-step difference, so the two
// cannot drift apart into two slightly different school markers.
// The 40 in the viewBox is the DRAWING coordinate system, not the display size, and
// it deliberately does not track `SCHOOL_MARKER_PX`: both consumers set the rendered
// size explicitly (a CSS width/height on V2's <img>, `scaledSize` on the legacy
// icon), and an SVG scales to either without loss. Changing the badge's size means
// changing that constant alone.
const SCHOOL_SVG = [
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40">',
  '<rect x="2.5" y="2.5" width="35" height="35" rx="11" fill="',
  SCHOOL_BADGE_COLOR,
  '" stroke="#ffffff" stroke-width="3"/>',
  '<g transform="translate(8 8)"><path d="',
  SCHOOL_GLYPH,
  '" fill="#ffffff"/></g>',
  '</svg>',
].join('');

export const SCHOOL_ICON_DATA_URI = 'data:image/svg+xml;charset=UTF-8,'.concat(
  encodeURIComponent(SCHOOL_SVG),
);

// A coordinate, or null. A STRING that parses is accepted (a hand-edited config is
// the normal way one of these arrives), but NaN/Infinity/'' are not — and 0 survives,
// because lat 0 and lng 0 are real coordinates.
function coord(value) {
  const num = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (typeof num !== 'number') return null;
  return Number.isFinite(num) ? num : null;
}

// PURE, and it takes the config rather than reading it: the fallback and the
// coercion are the whole decision here, and neither should need a BuildConfig in
// scope to be tested. `variants.contract.spec.js` runs the estate's real committed
// configs through this, so a malformed campus fails CI rather than vanishing.
export function resolveSchoolLocations(config) {
  const declared = config && config.SCHOOL_LOCATIONS;
  const clean = (Array.isArray(declared) ? declared : [])
    .map(entry => {
      if (!entry) return null;
      const lat = coord(entry.lat);
      const lng = coord(entry.lng);
      if (lat === null || lng === null) return null;
      return { lat, lng, name: entry.name ? String(entry.name) : '' };
    })
    .filter(Boolean);
  if (clean.length) return clean;

  const centre = (config && config.DEFAULT_MAP_CENTER) || null;
  const lat = coord(centre && centre.lat);
  const lng = coord(centre && centre.lng);
  if (lat === null || lng === null) return [];
  return [{ lat, lng, name: '' }];
}

// How many entries the config DECLARED versus how many survived. Read only by the
// variants contract spec: it is the difference between "this school has no campus
// list" (fine, the centre covers it) and "this school's campus list has an entry
// nothing will ever draw" (a config bug, and silent without this).
export function countDeclaredSchoolLocations(config) {
  const declared = config && config.SCHOOL_LOCATIONS;
  return Array.isArray(declared) ? declared.length : 0;
}

export function schoolLocations() {
  return resolveSchoolLocations(BuildConfig);
}
